import {
    InteractionRequiredAuthError,
    PublicClientApplication,
} from '@azure/msal-browser';

const AUTHORITY = 'https://login.microsoftonline.com/a6cc90df-f580-49dc-903f-87af5a75338e/v2.0';

const LOGIN_SCOPES = ['User.Read', 'openid', 'profile', 'email', 'User.ReadBasic.All'];

let msalInstance = null;
let pendingSsoRedirectLogin = false;

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

export function getMsalClientId() {
    switch (window.location.hostname) {
        case 'porocanje.uni-lj.si':
            return '9ed42378-d80d-411b-b6df-9e4f17abeb02';
        case 'porocanje-test.uni-lj.si':
        default:
            return '42dd1584-76ac-4b50-af1e-36dcb73070dc';
    }
}

// Used by silent refresh and Graph popup flows (v5 bridge page).
export function getMsalRedirectUri() {
    return window.location.origin + '/blank.html';
}

// Used by loginRedirect — app root is registered in Entra and avoids bridge navigation loops.
export function getAppLoginRedirectUri() {
    return window.location.origin;
}

export function getLoginRequest() {
    return {
        scopes: LOGIN_SCOPES,
        redirectUri: getAppLoginRedirectUri(),
    };
}

export function createMsalConfig() {
    return {
        auth: {
            clientId: getMsalClientId(),
            authority: AUTHORITY,
            redirectUri: getMsalRedirectUri(),
        },
        cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false,
        },
    };
}

export async function initializeMsal() {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(createMsalConfig());
        await msalInstance.initialize();
    }

    return msalInstance;
}

const MSAL_TEMP_CACHE_KEYS = [
    'request.origin',
    'urlHash',
    'request.params',
    'code.verifier',
    'request.native',
];

export function clearStaleMsalRedirectState() {
    const clientId = getMsalClientId();
    sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);

    MSAL_TEMP_CACHE_KEYS.forEach((key) => {
        sessionStorage.removeItem(`msal.${clientId}.${key}`);
    });
}

export function hasMsalAuthResponseInUrl(location = window.location) {
    const fragment = (location.hash || '').replace(/^#/, '');
    const query = (location.search || '').replace(/^\?/, '');

    return /(^|&)(code|error)=/.test(fragment)
        || /(^|&)(code|error)=/.test(query);
}

function getMsalRedirectHashFragment() {
    const hash = window.location.hash || '';

    if (hash && hasMsalAuthResponseInUrl({ hash })) {
        return hash.startsWith('#') ? hash.slice(1) : hash;
    }

    return null;
}

function primeOrphanedMsalRedirectState(clientId, hashFragment) {
    if (!sessionStorage.getItem(MSAL_INTERACTION_STATUS_KEY)) {
        sessionStorage.setItem(
            MSAL_INTERACTION_STATUS_KEY,
            JSON.stringify({ clientId, type: 'signin' }),
        );
    }

    sessionStorage.setItem(`msal.${clientId}.urlHash`, hashFragment);
}

function cleanupFailedMsalRedirect() {
    if (!hasMsalAuthResponseInUrl()) {
        return;
    }

    clearStaleMsalRedirectState();

    const loginPath = '/login/';

    if (window.location.pathname === loginPath || window.location.pathname.endsWith('/login')) {
        window.history.replaceState(null, '', loginPath);
        return;
    }

    window.location.replace(window.location.origin + loginPath);
}

export function markPendingSsoRedirectLogin() {
    pendingSsoRedirectLogin = true;
}

export function clearPendingSsoRedirectLogin() {
    pendingSsoRedirectLogin = false;
}

export function isPendingSsoRedirectLogin() {
    return pendingSsoRedirectLogin;
}

async function handleRedirectPromiseWithOptions(instance, hashFragment) {
    const options = { navigateToLoginRequestUrl: false };

    if (hashFragment) {
        options.hash = hashFragment;
    }

    return instance.handleRedirectPromise(options);
}

export async function handleMsalRedirectOnStartup() {
    if (window.location.pathname.endsWith('/blank.html')) {
        return null;
    }

    const instance = await initializeMsal();
    const hashFragment = getMsalRedirectHashFragment();

    try {
        let response = await handleRedirectPromiseWithOptions(instance, hashFragment);

        if (!response && hashFragment) {
            primeOrphanedMsalRedirectState(getMsalClientId(), hashFragment);
            response = await handleRedirectPromiseWithOptions(instance, hashFragment);
        }

        if (!response && hashFragment) {
            cleanupFailedMsalRedirect();
        }

        return response;
    } catch (error) {
        cleanupFailedMsalRedirect();
        throw error;
    }
}

export async function logoutMsal() {
    try {
        const instance = await initializeMsal();
        const accounts = instance.getAllAccounts();

        if (accounts.length > 0) {
            await instance.clearCache({ account: accounts[0] });
        }
    } catch (error) {
        console.warn('MSAL logout cleanup failed:', error);
    }

    clearStaleMsalRedirectState();
}

export async function getMsalInstance(existingInstance) {
    if (existingInstance) {
        return existingInstance;
    }

    return initializeMsal();
}

export async function acquireGraphToken(msalClient, scopes) {
    const accounts = msalClient.getAllAccounts();

    if (!accounts.length) {
        throw new Error('No signed-in Microsoft account found.');
    }

    const tokenRequest = {
        scopes,
        account: accounts[0],
        redirectUri: getMsalRedirectUri(),
    };

    try {
        return await msalClient.acquireTokenSilent(tokenRequest);
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            return await msalClient.acquireTokenPopup(tokenRequest);
        }
        throw error;
    }
}

export function isUserCancelledError(error) {
    return error?.errorCode === 'user_cancelled'
        || error?.message?.includes('User cancelled');
}

export function isInteractionInProgressError(error) {
    return error?.errorCode === 'interaction_in_progress';
}
